import { useAppDispatch, useAppSelector, useDebouncedCallback } from '@/hooks';
import { useGetQueueByIdQuery } from '@/services/queues';
import {
  useGetRequirementFlagSettingQuery,
  useGetRequirementFlagSettingsQuery,
} from '@/services/requirementFlagSettings';
import { removeAttachment } from '@/store/reducers/flaggingModeSlice';
import { PalmettoAutocomplete, PalmettoIcon, PalmettoTextInput } from '@palmetto/dsm-components';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AllDocumentTypes, documentTitles, FlagPurposeType, FlagReason, RequirementFlagSettingsType } from 'types';

// Debounce window for persisting wizard field edits (note/reason/criterion/problems) as a
// draft — long enough to collapse a burst of keystrokes into one PATCH, short enough that
// navigating away right after typing still reliably flushes (see useDebouncedCallback).
const WIZARD_DRAFT_DEBOUNCE_MS = 500;

// The subset of ChecklistFlagFlowState that maps onto a ChecklistPendingFlagDecision draft.
// `selectedFiles` is deliberately excluded — a raw File can't be serialized pre-upload (see
// the residual-gap note in the FIN-5030 plan).
export interface ChecklistFlagWizardDraft {
  note: string;
  selectedReason: FlagReason | null;
  selectedCriterionId: string | null;
  selectedProblems: { label: string; value: string }[];
}

import { ChecklistFlagCriteriaSection } from './ChecklistFlagCriteriaSection';
export { flagPurposeTypeStyles } from './ChecklistFlagCreationFlow.helpers';
import { Chip } from '@/dsm-components';
import { trimFileNames } from '@/helpers/trimFileNames';

import { WHITE_STYLE_CONST, flagPurposeTypeStyles, PURPOSE_TYPE_LABELS } from './ChecklistFlagCreationFlow.helpers';

export interface ChecklistFlagFlowState {
  note: string;
  selectedFiles: File[];
  selectedCriterionId: string | null;
  selectedProblems: { label: string; value: string }[];
  selectedReason: FlagReason | null;
  localRequirementId: string | undefined;
  flagType: RequirementFlagSettingsType | undefined;
  dataKey: string | undefined;
}

export interface ChecklistFlagCreationFlowProps {
  queueId: string;
  itemName: string;
  preselectedRequirementId?: string;
  preselectedPurposeType?: FlagPurposeType;
  preselectedNote?: string;
  currentIndex?: number;
  totalCount?: number;
  savedState?: ChecklistFlagFlowState;
  isSaving?: boolean;
  saveError?: string | null;
  allowedDocumentTypes?: AllDocumentTypes[];
  onNext?: (state: ChecklistFlagFlowState) => void;
  onBack?: (state: ChecklistFlagFlowState) => void;
  onComplete: (state: ChecklistFlagFlowState) => void;
  onCancel: () => void;
  // Persists note/reason/criterion/problems as a draft, debounced, while this flow is open —
  // keyed by `currentIndex` (frozen at the moment of the edit, not read again at flush time)
  // so a flush after the reviewer has already advanced to a different flag still updates the
  // correct one, not whichever flow happens to be active when the debounce fires.
  onDraftChange?: (flowIndex: number, draft: ChecklistFlagWizardDraft) => void;
}

export const ChecklistFlagCreationFlow: React.FC<ChecklistFlagCreationFlowProps> = ({
  queueId,
  itemName,
  preselectedRequirementId,
  preselectedPurposeType,
  preselectedNote,
  currentIndex = 0,
  totalCount = 1,
  savedState,
  isSaving = false,
  saveError,
  allowedDocumentTypes,
  onNext,
  onBack,
  onComplete,
  onCancel,
  onDraftChange,
}) => {
  const dispatch = useAppDispatch();
  const pendingAttachmentsByFlow = useAppSelector((state) => state.flaggingMode.pendingAttachments);
  const pendingAttachments = pendingAttachmentsByFlow[currentIndex] ?? [];
  const isFlaggingModeActive = useAppSelector((state) => state.flaggingMode.isActive);

  const selectedPurposeType = preselectedPurposeType ?? null;
  // Seed from a pre-filled evaluation reason when no saved state exists yet, but stay editable.
  const [note, setNote] = useState(savedState?.note ?? preselectedNote ?? '');
  const [selectedFiles, setSelectedFiles] = useState<File[]>(savedState?.selectedFiles ?? []);

  const [selectedCriterionId, setSelectedCriterionId] = useState<string | null>(
    savedState?.selectedCriterionId ?? null,
  );
  const [selectedProblems, setSelectedProblems] = useState<{ label: string; value: string }[]>(
    savedState?.selectedProblems ?? [],
  );
  const [selectedReason, setSelectedReason] = useState<FlagReason | null>(savedState?.selectedReason ?? null);
  const [localRequirementId, setLocalRequirementId] = useState<string | undefined>(savedState?.localRequirementId);

  const effectiveRequirementId = preselectedRequirementId ?? localRequirementId;

  const { data: fullSettingData, isLoading: isLoadingSetting } = useGetRequirementFlagSettingQuery(
    { id: effectiveRequirementId! },
    { skip: !effectiveRequirementId },
  );

  const { data: flagSettingsData } = useGetRequirementFlagSettingsQuery(
    { type: undefined, pageSize: 1000 },
    { skip: !!preselectedRequirementId },
  );

  const flagSettingOptions = useMemo((): { label: string; value: string }[] => {
    const settings = flagSettingsData?.requirementFlagSettings ?? [];
    return settings
      .map((s: { name: string; id?: { toString(): string } }) => ({
        label: s.name,
        value: s.id?.toString() ?? '',
      }))
      .toSorted((a: { label: string; value: string }, b: { label: string; value: string }) =>
        a.label.localeCompare(b.label),
      );
  }, [flagSettingsData]);

  const handleRequirementChange = useCallback((details: { value: string[] }) => {
    setLocalRequirementId(details.value[0] || undefined);
    setSelectedCriterionId(null);
    setSelectedProblems([]);
    setSelectedReason(null);
  }, []);

  const { data: queue } = useGetQueueByIdQuery({ id: queueId });
  const accountState = queue?.account?.address?.state;

  const isRequirementReady = effectiveRequirementId ? !!fullSettingData : true;
  const hasCriteria = (fullSettingData?.acceptanceCriteria?.length ?? 0) > 0;

  const selectedCriterion = fullSettingData?.acceptanceCriteria?.find(
    (c) => c.id.toString() === selectedCriterionId,
  );
  const criterionHasProblems = (selectedCriterion?.problems?.length ?? 0) > 0;

  const isLastFlag = currentIndex >= totalCount - 1;
  const isFirstFlag = currentIndex === 0;

  const canSubmit =
    isRequirementReady &&
    !isSaving &&
    (!hasCriteria || (!!selectedCriterionId && (!criterionHasProblems || selectedProblems.length > 0))) &&
    (hasCriteria || !fullSettingData || !!selectedReason);

  const collectState = useCallback(
    (): ChecklistFlagFlowState => ({
      note,
      selectedFiles,
      selectedCriterionId,
      selectedProblems,
      selectedReason,
      localRequirementId,
      flagType: fullSettingData?.type,
      dataKey: fullSettingData?.dataKey,
    }),
    [
      note,
      selectedFiles,
      selectedCriterionId,
      selectedProblems,
      selectedReason,
      localRequirementId,
      fullSettingData,
    ],
  );

  const handleAdvance = useCallback(() => {
    if (!isRequirementReady) return;
    const state = collectState();
    if (!isLastFlag && onNext) {
      onNext(state);
    } else {
      onComplete(state);
    }
  }, [isRequirementReady, isLastFlag, onNext, onComplete, collectState]);

  const handleNoteChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setNote(e.target.value);
  }, []);

  const handleFilesChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setSelectedFiles((prev) => [...prev, ...files]);
  }, []);

  const handleFileInputClick = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
    e.currentTarget.value = '';
  }, []);

  const handleRemoveFile = useCallback((index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleBack = useCallback(() => {
    onBack?.(collectState());
  }, [onBack, collectState]);

  const handleCriterionChange = useCallback((id: string | null) => {
    setSelectedCriterionId(id);
    setSelectedProblems([]);
  }, []);

  // Persist note/reason/criterion/problems as a draft while this flow is open — same fields
  // ChecklistPendingFlagDecision carries, everything except `selectedFiles`. `currentIndex`
  // is captured in the debounced call's closure at the moment of the edit (not read again at
  // flush time), so a later flush still targets the flag the reviewer was actually editing.
  const debouncedDraftChange = useDebouncedCallback(
    (flowIndex: number, draft: ChecklistFlagWizardDraft) => onDraftChange?.(flowIndex, draft),
    WIZARD_DRAFT_DEBOUNCE_MS,
  );
  // Skips the change-triggered persist on first mount — a freshly-opened flow (blank or
  // pre-filled from `preselectedNote`/a reopened draft) hasn't been edited yet.
  const isFirstRenderRef = useRef(true);
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    if (!onDraftChange) return;
    debouncedDraftChange(currentIndex, { note, selectedReason, selectedCriterionId, selectedProblems });
    // currentIndex/onDraftChange/debouncedDraftChange are stable for this flow instance's
    // lifetime (ChecklistPanel remounts it via a `key` per flow step) — only the field values
    // below should retrigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note, selectedReason, selectedCriterionId, selectedProblems]);

  return (
    <div className="flex flex-col gap-[14px]">
      <button
        type="button"
        onClick={onCancel}
        className="flex cursor-pointer items-center gap-4 self-start text-[14px] text-text-secondary hover:text-text-primary"
      >
        <PalmettoIcon name="chevronLeft" size="sm" />
        <span>Back to checklist</span>
      </button>

      <div className="flex flex-col gap-16 rounded-8 bg-canvas-bold p-16">
        <span className="text-[16px] font-bold text-text-primary">
          Flag {currentIndex + 1} of {totalCount}: {itemName}
        </span>

        <div className="border-t border-border-default" />

        <div className="flex flex-col gap-16">
          {selectedPurposeType && (
            <div className="flex flex-col gap-6">
              <label className="text-[12px] font-bold text-text-secondary">Flag Type</label>
              <div
                className={`inline-flex self-start rounded-10 px-12 py-8 text-[12px] font-bold ${flagPurposeTypeStyles[selectedPurposeType]}`}
              >
                <span>{PURPOSE_TYPE_LABELS[selectedPurposeType]}</span>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-6">
            {preselectedRequirementId ? (
              <>
                <label className="text-[12px] font-bold text-text-secondary">Flag Requirement</label>
                <span className="text-[14px] text-text-primary">
                  {isLoadingSetting ? 'Loading…' : (fullSettingData?.name ?? '—')}
                </span>
              </>
            ) : (
              <PalmettoAutocomplete
                key={flagSettingsData ? 'loaded' : 'loading'}
                label="Flag Requirement"
                items={[
                  { label: 'None', value: '', selected: !localRequirementId },
                  ...flagSettingOptions.map((opt) => ({
                    ...opt,
                    selected: opt.value === localRequirementId,
                  })),
                ]}
                onValueChange={handleRequirementChange}
                maxMenuHeight={500}
              />
            )}
          </div>

          {(effectiveRequirementId || isLoadingSetting) && (
            <ChecklistFlagCriteriaSection
              setting={fullSettingData}
              isLoadingSetting={isLoadingSetting}
              accountState={accountState}
              selectedCriterionId={selectedCriterionId}
              selectedProblems={selectedProblems}
              selectedReason={selectedReason}
              onCriterionChange={handleCriterionChange}
              onProblemsChange={setSelectedProblems}
              onReasonChange={setSelectedReason}
            />
          )}

          <div className="flex flex-col gap-6">
            <PalmettoTextInput id="flag-note" label="Notes" value={note} onChange={handleNoteChange} />

            <label className="flex cursor-pointer items-center gap-6 self-start">
              <PalmettoIcon name="paperClip" size="xs" className="text-btn-content-text-idle" />
              <span className="text-[12px] font-bold text-btn-content-text-idle">Upload files</span>
              <span className="text-[10px] text-text-secondary">jpg, png, gif</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif"
                multiple
                className="hidden"
                onChange={handleFilesChange}
                onClick={handleFileInputClick}
              />
            </label>

            {selectedFiles.length > 0 && (
              <div className="flex flex-col gap-4">
                {selectedFiles.map((file, idx) => (
                  <div key={`${file.name}-${idx}`} className="flex items-center gap-6">
                    <span className="text-[12px] text-text-secondary">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(idx)}
                      className="leading-none text-text-tertiary hover:text-text-primary"
                      aria-label={`Remove ${file.name}`}
                    >
                      <PalmettoIcon name="x" size="xs" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {isFlaggingModeActive && allowedDocumentTypes && allowedDocumentTypes.length > 0 && (
            <div className="flex flex-col gap-6">
              <span className="text-[12px] font-bold text-text-secondary">Attached package documents</span>
              <div className="flex flex-col gap-4">
                <span className="text-[11px] text-text-tertiary">
                  Documents can be attached from the following sections:
                </span>
                <div className="flex flex-wrap gap-4">
                  {allowedDocumentTypes.map((documentType) => (
                    <Chip key={documentType} variant="success">
                      {documentTitles[documentType] || documentType}
                    </Chip>
                  ))}
                </div>
              </div>
              {pendingAttachments.length === 0 ? (
                <p className="text-[12px] text-text-tertiary">
                  Select documents from the package list to attach as evidence.
                </p>
              ) : (
                <div className="flex flex-col gap-4">
                  {pendingAttachments.map((attachment) => (
                    <div key={attachment.id} className="mt-4 flex items-start gap-6">
                      <PalmettoIcon name="file" size="xs" className="text-text-secondary" />
                      <div className="flex min-w-0 flex-1 flex-col gap-2">
                        {attachment.name.map((fileName) => {
                          const truncatedName = trimFileNames(fileName);

                          return (
                            <span
                              key={fileName}
                              className="max-w-[200px] text-[12px] text-text-secondary"
                              title={fileName}
                            >
                              {truncatedName}
                            </span>
                          );
                        })}
                      </div>
                      <button
                        type="button"
                        onClick={() => dispatch(removeAttachment(attachment.id))}
                        className="leading-none text-text-tertiary hover:text-text-primary"
                        aria-label={`Remove ${attachment.name.join(', ')}`}
                      >
                        <PalmettoIcon name="x" size="xs" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {saveError && <p className="text-text-danger text-xs">{saveError}</p>}

        <div className="flex items-center gap-10">
          <button
            type="button"
            aria-label={isLastFlag ? (totalCount === 1 ? 'Save flag' : `Save ${totalCount} flags`) : 'Next flag'}
            onClick={handleAdvance}
            disabled={!canSubmit}
            className="cursor-pointer rounded-12 bg-btn-bg-primary-idle px-[19px] py-[15px] text-[14px] font-bold disabled:opacity-50"
          >
            <span style={WHITE_STYLE_CONST}>
              {isLastFlag ? `Save ${totalCount} Flag${totalCount === 1 ? '' : 's'}` : 'Next Flag'}
            </span>
          </button>
          <button
            type="button"
            onClick={isFirstFlag ? onCancel : handleBack}
            disabled={isSaving}
            className="cursor-pointer text-[14px] font-bold text-btn-content-text-idle disabled:opacity-50"
          >
            {isFirstFlag ? 'Cancel' : 'Back'}
          </button>
        </div>
      </div>
    </div>
  );
};
